import { ColorInput } from 'common-front/src/designSystem/components/colorInput';
import { FileInput, FileInputValue } from 'common-front/src/designSystem/components/file/fileInput';
import { RichEditor } from 'common-front/src/designSystem/components/richEditor/richEditor';
import { Select } from 'common-front/src/designSystem/components/select/select';
import { Separator } from 'common-front/src/designSystem/components/separator';
import { TextInput } from 'common-front/src/designSystem/components/textInput';
import { ToggleText } from 'common-front/src/designSystem/components/toggle';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { Accept } from 'common-front/src/util/accept';
import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { Spacer } from 'common/src/designSystem/components/spacer';
import { UserPlanning } from 'common/src/documents/userPlanning/userPlanning';
import { DocumentType, UserPlanningDocumentQuery } from 'common/src/generated/types';
import { usePossibleFontsOptions } from 'common/src/util/font';
import { HeaventPaths } from 'common/src/util/paths/heaventPaths';
import { isNonEmptyString } from 'common/src/util/string';
import { getDocumentHref } from 'common/src/util/url';
import { getDefaultFileOptions, getDefaultUserPlanningOptions } from 'common/src/vo/eventDocument';
import * as React from 'react';
import { useUserPlanningDocumentQuery } from '../../generated/graphqlHooks';
import { DocumentHeader } from './documentHeader';
import { DocumentOptions, useDocumentOptions } from './documentOptions';
import { DocumentsAssignedUserSelector } from './documentsAssignedUserSelector';
import { DocumentShadow } from './documentShadow';

interface IUserPlanningContentProps {
    data: UserPlanningDocumentQuery;
}

const UserPlanningContent = (props: IUserPlanningContentProps) => {
    const {
        translate,
        params: { organizationId, eventId }
    } = useHeavent();
    const {
        eventDocument,
        informationsTitle,
        informationsContent,
        options,
        setOptions,
        setInformationsTitle,
        setInformationsContent
    } = useDocumentOptions(
        props.data.event.document.options,
        getDefaultUserPlanningOptions(),
        eventId,
        {
            id: props.data.event.document.id,
            informationsTitle: props.data.event.document.informationsTitle,
            informationsContent: props.data.event.document.informationsContent
        }
    );
    const fontsOptions = usePossibleFontsOptions();
    const ui = props.data.event?.volunteerRegistration.userInfo;
    const link = ui.links?.userPlanningLink ?? '';

    return (
        <Flex css={{ flex: '1', overflow: 'auto' }} gap="7" width={1}>
            <DocumentOptions>
                <Flex align="center" gap="3">
                    <FileInput
                        accept={Accept.Images}
                        format="base64"
                        label={translate('fond_du_documen_51758')}
                        placeholder={translate('fond_du_documen_51758')}
                        value={options.background}
                        onChange={(background: FileInputValue) => {
                            setOptions({ background });
                        }}
                    />

                    {isNonEmptyString(options.background?.name) && (
                        <Box
                            css={{ cursor: 'pointer' }}
                            font="gray700 textSm medium"
                            onClick={() => {
                                setOptions({ background: getDefaultFileOptions() });
                            }}
                        >
                            {translate('r_initialiser_77538')}
                        </Box>
                    )}
                </Flex>

                <Separator direction="horizontal" />

                <Select
                    label="Police"
                    value={options.fontFamily}
                    onChange={(fontFamily) => {
                        setOptions({ fontFamily });
                    }}
                >
                    {fontsOptions}
                </Select>

                <Select
                    label={translate('police_des_titr_37497')}
                    value={options.titleFontFamily}
                    onChange={(titleFontFamily) => {
                        setOptions({ titleFontFamily });
                    }}
                >
                    {fontsOptions}
                </Select>

                <ColorInput
                    label={translate('couleur_des_sou_18968')}
                    value={options.subtitleColor}
                    onChange={(subtitleColor: string) => {
                        setOptions({ subtitleColor });
                    }}
                />

                <ColorInput
                    label={translate('couleur_du_text_32021')}
                    value={options.textColor}
                    onChange={(textColor: string) => {
                        setOptions({ textColor });
                    }}
                />

                <ColorInput
                    label={translate('couleur_du_cont_17150')}
                    value={options.headerColor}
                    onChange={(headerColor: string) => {
                        setOptions({ headerColor });
                    }}
                />

                <Separator direction="horizontal" />

                <ToggleText
                    value={options.showInformations}
                    onChange={(showInformations) => {
                        setOptions({ showInformations });
                    }}
                >
                    {translate('afficher_la_sec_84744')}
                </ToggleText>

                {options.showInformations && (
                    <Flex
                        css={{
                            background: '$gray50',
                            border: '1px solid $gray200',
                            borderRadius: '$1',
                            padding: '$4'
                        }}
                        direction="column"
                        gap="3"
                    >
                        <TextInput
                            label={translate('titre_de_la_sec_41575')}
                            value={informationsTitle}
                            onChange={(newInformationsTitle) => {
                                setInformationsTitle(newInformationsTitle);
                            }}
                        />

                        <RichEditor
                            initialValue={informationsContent}
                            label={translate('contenu_de_la_s_43102')}
                            onChange={(newInformationsContent) => {
                                setInformationsContent(newInformationsContent);
                            }}
                        />
                    </Flex>
                )}

                <ToggleText
                    value={options.showQrCode}
                    onChange={(showQrCode) => {
                        setOptions({ showQrCode });
                    }}
                >
                    {translate('afficher_le_qr_15635')}
                </ToggleText>
            </DocumentOptions>

            <Flex css={{ flex: '1' }} direction="column">
                <DocumentsAssignedUserSelector
                    downloadPath={getDocumentHref(link, 'pdf', { lang: ui?.language })}
                    getPath={(newUserInfoId) =>
                        HeaventPaths.DOCUMENTS_USER_PLANNING(organizationId, eventId, newUserInfoId)
                    }
                />

                <DocumentShadow>
                    <UserPlanning
                        event={props.data.event}
                        eventDocument={eventDocument}
                        options={options}
                    />
                </DocumentShadow>
            </Flex>
        </Flex>
    );
};

export const UserPlanningDocument = () => {
    const {
        translate,
        params: { organizationId, eventId, userInfoId }
    } = useHeavent();
    const { data, loader } = useUserPlanningDocumentQuery({ eventId, userInfoId });

    return (
        <Flex css={{ overflowY: 'auto', padding: '$7' }} direction="column" height={1} width={1}>
            <DocumentHeader
                documentType={DocumentType.UserPlanning}
                endAt={data.event?.endAt}
                eventId={eventId}
                languages={data.event?.languages ?? []}
                organizationId={organizationId}
                segmentsFolders={data.event?.segmentsFolders ?? []}
                showDate={true}
                startAt={data.event?.startAt}
                title={translate('plannings_indiv_80073')}
            />

            <Spacer height="7" />

            {loader || <UserPlanningContent data={data} />}
        </Flex>
    );
};
